import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import { IoCloseSharp } from 'react-icons/io5';
import { appendDepreciationNodeToPipeline, editDepreciationNode, getGlobalParams } from '../../../api/api';
import { usePipeline } from '../../../context/PipelineContext';
import { validateNodeName } from '../../../name_validation';
import { notifyError } from '../../../errors/notifyError';
import { OP } from '../../../api/operations';
import './DepreciationConfigModal.css';

// Set the app element for accessibility
try {
  Modal.setAppElement('#root');
} catch (error) {
  console.warn('Could not set app element for Modal:', error);
  // This prevents the app from crashing if the root element isn't available
}

const isBlank = (value) => value === '' || value === null || value === undefined;

const parseOptionalPercent = (value) => {
  if (isBlank(value)) return 0;
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const isInvalidOptionalPercent = (value) => {
  if (isBlank(value)) return false;
  const parsed = parseFloat(value);
  return !Number.isFinite(parsed) || parsed < 0 || parsed > 100;
};

const customStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    borderRadius: '0.375rem',
    padding: '1.5rem',
    maxWidth: '500px',
    width: '90%',
    position: 'relative',
  },
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 9999, // Higher z-index to ensure it appears on top
  }
};

const DepreciationConfigModal = ({ isOpen, onClose, onConfigured, initialData = {} }) => {
  // Simple state initialization
  const [nodeName, setNodeName] = useState(initialData.name || '');
  const [nodeNameError, setNodeNameError] = useState('');
  const [basicPriceWind, setBasicPriceWind] = useState(initialData.BasicPriceWind ?? '');
  const [basicPriceSolar, setBasicPriceSolar] = useState(initialData.BasicPriceSolar ?? '');
  const [basicPriceSolarBtm, setBasicPriceSolarBtm] = useState(
    initialData.BasicPriceSolarBtm !== undefined && initialData.BasicPriceSolarBtm !== null
      ? initialData.BasicPriceSolarBtm
      : 0
  );
  const [depRateForITWDV, setDepRateForITWDV] = useState(initialData.depRateForITWDV ?? '');
  const [addnDepRate, setAddnDepRate] = useState(initialData.addnDepRate ?? '');
  const [taxRate, setTaxRate] = useState(initialData.taxRate ?? '');
  const [gstInput, setGstInput] = useState(!!initialData.gstInput);
  const [gstPerc, setGstPerc] = useState(initialData.gstPerc ?? '');
  const { session } = usePipeline(); // Use the context to get the session
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingGlobal, setLoadingGlobal] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  
  // Determine if we're in edit mode (has a name AND isConfigured is true)
  const isEditing = !!initialData.name && initialData.isConfigured === true;
  
  // Keep track of whether the modal has been opened
  const [hasInitialized, setHasInitialized] = useState(false);
  
  // Update state only when the modal is opened or initialData changes before user interaction
  useEffect(() => {
    if (isOpen) {
      // Only initialize values if this is the first time opening or if the modal was closed and reopened
      if (!hasInitialized) {
        setNodeName(initialData.name || '');
        setBasicPriceWind(initialData.BasicPriceWind ?? '');
        setBasicPriceSolar(initialData.BasicPriceSolar ?? '');
        setBasicPriceSolarBtm(
          initialData.BasicPriceSolarBtm !== undefined && initialData.BasicPriceSolarBtm !== null
            ? initialData.BasicPriceSolarBtm
            : 0
        );
        setDepRateForITWDV(initialData.depRateForITWDV ?? '');
        setAddnDepRate(initialData.addnDepRate ?? '');
        setTaxRate(initialData.taxRate ?? '');
        setGstInput(!!initialData.gstInput);
        setGstPerc(initialData.gstPerc ?? '');
        
        setHasInitialized(true);
        
        console.log('Modal opened with data:', {
          initialData,
          isEditing: initialData.isEditing || false
        });
      }
    } else {
      // Reset the initialization flag when the modal is closed
      setHasInitialized(false);
    }
  }, [isOpen, initialData, hasInitialized]);
  
  // Load defaults from global store
  useEffect(() => {
    const loadGlobal = async () => {
      if (!isOpen) return;
      if (isEditing) return;
      if (!session?.pipeline_name || session.pipeline_name === 'Unsaved_Pipeline') return;
      setLoadingGlobal(true);
      try {
        const gp = await getGlobalParams(session.pipeline_name);
        if (gp?.Basic_price_wind != null) setBasicPriceWind(gp.Basic_price_wind);
        if (gp?.Basic_price_solar != null) setBasicPriceSolar(gp.Basic_price_solar);
        if (gp?.Basic_price_solar_btm != null) setBasicPriceSolarBtm(gp.Basic_price_solar_btm);
        if (gp?.depreciation_rate != null) setDepRateForITWDV(gp.depreciation_rate);
        if (gp?.addn_dep_rate != null) setAddnDepRate(gp.addn_dep_rate);
        if (gp?.gst_perc != null) setGstPerc(gp.gst_perc);
      } catch (e) {
        // ignore failures, keep local
      } finally {
        setLoadingGlobal(false);
      }
    };
    loadGlobal();
  }, [isOpen, isEditing, session?.pipeline_name]);

  // Validate node name
  useEffect(() => {
    // Always validate the node name, even if it's empty
    const validation = validateNodeName(nodeName);
    setNodeNameError(validation.isValid ? '' : validation.errorMessage);
  }, [nodeName]);

  const handleConfigure = async () => {
    try {
      setSubmitError(null);
      // Validate node name using the validation function
      const nameValidation = validateNodeName(nodeName);
      if (!nameValidation.isValid) {
        setNodeNameError(nameValidation.errorMessage);
        // No need for alert as the button will be disabled
        return;
      }

      // Make sure we have valid parameters
      if (isNaN(basicPriceWind) || parseFloat(basicPriceWind) < 0) {
        setSubmitError({ title: 'Invalid wind price', message: 'Please enter a valid Basic Price (Wind).' });
        return;
      }
      if (isNaN(basicPriceSolar) || parseFloat(basicPriceSolar) < 0) {
        setSubmitError({ title: 'Invalid solar price', message: 'Please enter a valid Basic Price (Solar).' });
        return;
      }
      // Solar (BTM) can be 0, but must be numeric and non-negative
      const solarBtmVal = (basicPriceSolarBtm === '' || basicPriceSolarBtm === null || basicPriceSolarBtm === undefined)
        ? 0
        : basicPriceSolarBtm;
      if (isNaN(solarBtmVal) || parseFloat(solarBtmVal) < 0) {
        setSubmitError({ title: 'Invalid BTM solar price', message: 'Please enter a valid Basic Price (Solar - BTM) (>= 0).' });
        return;
      }
      if (isInvalidOptionalPercent(depRateForITWDV)) {
        setSubmitError({ title: 'Invalid depreciation rate', message: 'Please enter a valid depreciation rate (0-100), or leave blank for 0.' });
        return;
      }
      if (isInvalidOptionalPercent(addnDepRate)) {
        setSubmitError({ title: 'Invalid additional depreciation rate', message: 'Please enter a valid additional depreciation rate (0-100), or leave blank for 0.' });
        return;
      }
      if (!taxRate || isNaN(taxRate) || parseFloat(taxRate) < 0 || parseFloat(taxRate) > 100) {
        setSubmitError({ title: 'Invalid tax rate', message: 'Please enter a valid tax rate (0-100).' });
        return;
      }
      if (isInvalidOptionalPercent(gstPerc)) {
        setSubmitError({ title: 'Invalid GST %', message: 'Please enter a valid GST percentage (0-100), or leave blank for 0.' });
        return;
      }

      const depRateVal = parseOptionalPercent(depRateForITWDV);
      const addnDepVal = parseOptionalPercent(addnDepRate);
      const gstPercVal = parseOptionalPercent(gstPerc);
      
      // Get the position from initialData
      const position = initialData.position || { x: 0, y: 0 };
      
      // Set submitting state to show loading indicator
      setIsSubmitting(true);
      
      console.log("Configuration mode:", {
        isEditing,
        name: initialData.name,
        isConfigured: initialData.isConfigured
      });
      
      // Make the API call if we have a valid pipeline name
      if (session.pipeline_name !== "Unsaved_Pipeline") {
        try {
          if (isEditing) {
            // We're editing an existing node
            console.log(`Calling API to edit depreciation node: ${initialData.name} in pipeline: ${session.pipeline_name}`);
            console.log(`New parameters - BP Wind: ${basicPriceWind}, BP Solar: ${basicPriceSolar}, Dep Rate: ${depRateForITWDV}, Addn Dep: ${addnDepRate}, Tax Rate: ${taxRate}`);
            
            await editDepreciationNode(
              session.pipeline_name,
              initialData.name,  // Use the original name
              parseFloat(basicPriceWind),
              parseFloat(basicPriceSolar),
              (solarBtmVal === '' ? 0 : parseFloat(solarBtmVal)),
              depRateVal,
              addnDepVal,
              parseFloat(taxRate),
              Boolean(gstInput),
              gstPercVal
            );
            
            console.log(`Successfully edited depreciation node ${initialData.name}`);
          } else {
            // We're creating a new node
            console.log(`Calling API to append depreciation node: ${nodeName} to pipeline: ${session.pipeline_name}`);
            console.log(`Parameters - BP Wind: ${basicPriceWind}, BP Solar: ${basicPriceSolar}, Dep Rate: ${depRateForITWDV}, Addn Dep: ${addnDepRate}, Tax Rate: ${taxRate}, Position: (${position.x}, ${position.y})`);
            
            await appendDepreciationNodeToPipeline(
              session.pipeline_name,
              nodeName,
              parseFloat(basicPriceWind),
              parseFloat(basicPriceSolar),
              (solarBtmVal === '' ? 0 : parseFloat(solarBtmVal)),
              depRateVal,
              addnDepVal,
              parseFloat(taxRate),
              Boolean(gstInput),
              gstPercVal,
              position.x,
              position.y
            );
            
            console.log(`Successfully added depreciation node ${nodeName} to backend`);
          }
        } catch (apiError) {
          console.error(`Error calling ${isEditing ? 'editDepreciationNode' : 'appendDepreciationNodeToPipeline'}:`, apiError);
          const info = notifyError(apiError, {
            operation: isEditing ? OP.node.editDepreciation : OP.node.appendDepreciation,
            showToast: false,
          });
          setSubmitError(info);
          // Don't continue with UI updates if API call fails
          setIsSubmitting(false);
          return;
        }
      } else {
        console.log(`Skipping API call for unsaved pipeline`);
      }
      
      // Call the callback to update the UI
      onConfigured({
        name: isEditing ? initialData.name : nodeName, // Keep original name in edit mode
        BasicPriceWind: parseFloat(basicPriceWind),
        BasicPriceSolar: parseFloat(basicPriceSolar),
        BasicPriceSolarBtm: (solarBtmVal === '' ? 0 : parseFloat(solarBtmVal)),
        depRateForITWDV: depRateVal,
        addnDepRate: addnDepVal,
        taxRate: parseFloat(taxRate),
        gstInput: Boolean(gstInput),
        gstPerc: gstPercVal
      });
      
      // Reset submitting state
      setIsSubmitting(false);
      
      // Note: We don't close the modal here as it will be closed by the parent component
    } catch (error) {
      console.error('Error configuring depreciation node:', error);
      const info = notifyError(error, { showToast: false });
      setSubmitError(info);
      setIsSubmitting(false);
      return; // Exit early on error
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      style={customStyles}
      contentLabel="Depreciation Configuration"
      shouldCloseOnOverlayClick={true}
      shouldCloseOnEsc={true}
      ariaHideApp={false} // Prevents warning about appElement
    >
      <div className="depreciation-modal">
        <button onClick={onClose} className="depreciation-modal-close">
          <IoCloseSharp size={24} />
        </button>
        
        <h2 className="depreciation-modal-title">
          {isEditing ? "Edit Depreciation Parameters" : "Configure New Depreciation Node"}
        </h2>

        {submitError && (
          <div
            role="alert"
            className="depreciation-modal-error"
            style={{
              marginTop: '0.75rem',
              marginBottom: '0.75rem',
              padding: '0.75rem',
              borderRadius: '0.375rem',
              background: 'rgba(239, 68, 68, 0.12)',
              border: '1px solid rgba(239, 68, 68, 0.35)',
              color: '#b91c1c',
              fontSize: '0.95rem',
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: '0.25rem' }}>{submitError.title}</div>
            <div>{submitError.message}</div>
          </div>
        )}
        
        <div className="depreciation-modal-field">
          <label className="depreciation-modal-label">
            Node Name: <span className="required">*</span>
          </label>
          {isEditing ? (
            <input
              type="text"
              value={nodeName}
              className="depreciation-modal-input"
              disabled={true}
              title="Node name cannot be changed in edit mode"
            />
          ) : (
            <input
              type="text"
              value={nodeName}
              onChange={(e) => setNodeName(e.target.value)}
              onBlur={() => {
                // Validate again on blur to ensure we show errors when user leaves the field
                const validation = validateNodeName(nodeName);
                setNodeNameError(validation.isValid ? '' : validation.errorMessage);
              }}
              className={`depreciation-modal-input ${nodeNameError ? 'error' : ''}`}
              placeholder="Enter node name (letters, digits, hyphens, underscores, periods)"
              aria-invalid={!!nodeNameError}
              aria-describedby={nodeNameError ? "node-name-error" : undefined}
            />
          )}
          {!isEditing && nodeNameError && (
            <div className="depreciation-modal-error" id="node-name-error" role="alert">{nodeNameError}</div>
          )}
          {!isEditing && !nodeNameError && (
            <div className="depreciation-modal-hint">
              Valid characters: letters, digits, hyphens (-), underscores (_), periods (.)
            </div>
          )}
        </div>
        
        <div className="depreciation-modal-field">
          <label className="depreciation-modal-label">
            Basic Price - Wind (Cr): <span className="required">*</span>
          </label>
          <input
            type="number"
            value={basicPriceWind}
            onChange={(e) => setBasicPriceWind(e.target.value)}
            className="depreciation-modal-input"
            placeholder="Enter Basic Price for Wind"
            step="0.01"
            min="0"
          />
        </div>

        <div className="depreciation-modal-field">
          <label className="depreciation-modal-label">
            Basic Price - Solar (Cr): <span className="required">*</span>
          </label>
          <input
            type="number"
            value={basicPriceSolar}
            onChange={(e) => setBasicPriceSolar(e.target.value)}
            className="depreciation-modal-input"
            placeholder="Enter Basic Price for Solar"
            step="0.01"
            min="0"
          />
        </div>

        <div className="depreciation-modal-field">
          <label className="depreciation-modal-label">
            Basic Price - Solar (BTM) (Cr): <span className="required">*</span>
          </label>
          <input
            type="number"
            value={basicPriceSolarBtm}
            onChange={(e) => setBasicPriceSolarBtm(e.target.value)}
            className="depreciation-modal-input"
            placeholder="Enter Basic Price for Solar (BTM) (0 if not applicable)"
            step="0.01"
            min="0"
          />
        </div>

        <div className="depreciation-modal-field">
          <label className="depreciation-modal-label">
            Depreciation Rate (IT WDV %):
          </label>
          <input
            type="number"
            value={depRateForITWDV}
            onChange={(e) => setDepRateForITWDV(e.target.value)}
            className="depreciation-modal-input"
            placeholder="Optional, 0 if none (e.g. 15)"
            step="0.01"
            min="0"
            max="100"
          />
        </div>

        <div className="depreciation-modal-field">
          <label className="depreciation-modal-label">
            Additional Depreciation Rate (%):
          </label>
          <input
            type="number"
            value={addnDepRate}
            onChange={(e) => setAddnDepRate(e.target.value)}
            className="depreciation-modal-input"
            placeholder="Optional, 0 if none (e.g. 10)"
            step="0.01"
            min="0"
            max="100"
          />
        </div>

        <div className="depreciation-modal-field">
          <label className="depreciation-modal-label">
            Tax Rate (%): <span className="required">*</span>
          </label>
          <input
            type="number"
            value={taxRate}
            onChange={(e) => setTaxRate(e.target.value)}
            className="depreciation-modal-input"
            placeholder="Enter tax rate (e.g. 23)"
            step="0.01"
            min="0"
            max="100"
          />
        </div>

        <div className="depreciation-modal-field">
          <label className="depreciation-modal-label">
            Apply GST Input Credit?
          </label>
          <input
            type="checkbox"
            checked={gstInput}
            onChange={(e) => setGstInput(e.target.checked)}
            className="depreciation-modal-checkbox"
          />
        </div>

        <div className="depreciation-modal-field">
          <label className="depreciation-modal-label">
            GST Percentage (%):
          </label>
          <input
            type="number"
            value={gstPerc}
            onChange={(e) => setGstPerc(e.target.value)}
            className="depreciation-modal-input"
            placeholder="Optional, 0 if none (e.g. 13.8)"
            step="0.01"
            min="0"
            max="100"
          />
        </div>
        
        <div className="depreciation-modal-button-container">
          <button 
            className="depreciation-modal-configure-button"
            onClick={handleConfigure}
            disabled={
              isSubmitting || 
              (!isEditing && (!!nodeNameError || !nodeName)) || 
              (isNaN(basicPriceWind) || parseFloat(basicPriceWind) < 0) ||
              (isNaN(basicPriceSolar) || parseFloat(basicPriceSolar) < 0) ||
              (basicPriceSolarBtm === '' ? false : (isNaN(basicPriceSolarBtm) || parseFloat(basicPriceSolarBtm) < 0)) ||
              isInvalidOptionalPercent(depRateForITWDV) ||
              isInvalidOptionalPercent(addnDepRate) ||
              isInvalidOptionalPercent(gstPerc) ||
              !taxRate || isNaN(taxRate)
            }
            title={
              nodeNameError ? nodeNameError : 
              (!nodeName && !isEditing) ? 'Node name is required' : 
              (isNaN(basicPriceWind) || parseFloat(basicPriceWind) < 0) ? 'Valid Basic Price (Wind) is required (> 0)' : 
              (isNaN(basicPriceSolar) || parseFloat(basicPriceSolar) < 0) ? 'Valid Basic Price (Solar) is required (> 0)' :
              (basicPriceSolarBtm === '' ? false : (isNaN(basicPriceSolarBtm) || parseFloat(basicPriceSolarBtm) < 0)) ? 'Valid Basic Price (Solar - BTM) is required (>= 0)' :
              isInvalidOptionalPercent(depRateForITWDV) ? 'Depreciation rate must be 0-100 if provided' :
              isInvalidOptionalPercent(addnDepRate) ? 'Additional depreciation rate must be 0-100 if provided' :
              isInvalidOptionalPercent(gstPerc) ? 'GST percentage must be 0-100 if provided' :
              !taxRate || isNaN(taxRate) ? 'Valid tax rate is required' : ''
            }
          >
            {isSubmitting ? 'Processing...' : 
              isEditing ? 'Save Changes' : 'Configure'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default DepreciationConfigModal;
